import { useSyncExternalStore } from "react";
import type { MotionValue } from "framer-motion";
import type { KeyframeBand, KeyframeIndex } from "./types";

/**
 * Pure resolver: given scroll progress and a band table, return the active
 * beat's 1-indexed `index`. Bands must be contiguous and sorted ascending by
 * `start`. Below the first band's start, returns the first band's index.
 */
export function beatIndexAtProgress(
  progress: number,
  bands: readonly KeyframeBand[],
): KeyframeIndex {
  for (let i = bands.length - 1; i >= 0; i--) {
    // i is in [0, bands.length - 1]; bounds guaranteed by the loop.
    const b = bands[i]!;
    if (progress >= b.start) return b.index;
  }
  // bands is always non-empty (callers pass module-level constants).
  return bands[0]!.index;
}

/**
 * Reads the active keyframe index from a motion-value `progress`.
 * Safe for SSR: `getServerSnapshot` calls `progress.get()` so the SSR
 * output matches the first client render (both read the same initial value,
 * which is 0 in production). Falls back to `1` if `bands` is empty — the
 * pure resolver `beatIndexAtProgress` throws on empty input; this hook
 * shields the SSR path only.
 *
 * `bands` must be referentially stable across renders. Module-level
 * `KEYFRAME_BANDS`/`PIN1_BANDS`/`PIN2_BANDS` are; do not pass `bands.slice(...)`
 * inline or the subscription will thrash.
 */
export function useBeatIndex(
  progress: MotionValue<number>,
  bands: readonly KeyframeBand[],
): KeyframeIndex {
  const safeSnapshot = (value: number): KeyframeIndex =>
    bands.length === 0 ? (1 as KeyframeIndex) : beatIndexAtProgress(value, bands);
  return useSyncExternalStore(
    (cb) => progress.on("change", cb),
    () => safeSnapshot(progress.get()),
    () => safeSnapshot(progress.get()),
  );
}
